import { z } from "zod";
import { LocalizedTextSchema } from "./common.js";

/** Anything the store, mail, events or passes can hand out (60-store.md, 70-events.md). */
export const BundleSchema = z.object({
  resources: z.record(z.string(), z.number()).optional(),
  items: z.array(z.object({ itemId: z.string(), qty: z.number().int().min(1) })).optional(),
  credits: z.number().int().min(0).optional(),
  units: z.array(z.string()).optional(),
  cosmetics: z.array(z.string()).optional(),
  vipPoints: z.number().int().min(0).optional(),
});
export type Bundle = z.infer<typeof BundleSchema>;

export const PackTierSchema = z.enum(["starter", "bronze", "silver", "gold", "platinum", "diamond"]);
export type PackTier = z.infer<typeof PackTierSchema>;

export interface PackDef {
  id: string;
  tier: PackTier;
  name: { de: string; en: string };
  price: number;
  contents: Bundle;
  /** Starter: one purchase per account, never doubled. */
  once: boolean;
  doubleFirst: boolean;
}

export type DealKind = "daily" | "mustbuy" | "weekly" | "monthly" | "flash";

export interface DealDef {
  id: string;
  kind: DealKind;
  name: { de: string; en: string };
  price: number;
  contents: Bundle;
  /** Purchases per period (day, week, month or flash window). */
  limit: number;
  /** Daily deal slot price the pool belongs to (daily only). */
  slot?: number;
  /** Flash offers: window length in hours. */
  hours?: number;
  /** Minimum HQ for the pool entry. */
  minHq?: number;
}

export type ChainTrigger = "hq.levelup" | "first.defeat" | "pull.s" | "research.finished" | "resources.low" | "absent.3d" | "vip.levelup" | "hive.failed";

export interface ChainOfferDef {
  id: string;
  trigger: ChainTrigger;
  name: { de: string; en: string };
  cooldownHours: number;
  /** Three escalating steps; each step unlocks after the previous purchase. */
  steps: { price: number; contents: Bundle }[];
  /** HQ-scaled chains multiply prices and contents by hq / 5 (min 1). */
  scalesWithHq?: boolean;
}

export type PassKind = "nexus" | "weekly" | "battle" | "growth" | "unit" | "fabrication";

export interface PassDef {
  id: string;
  kind: PassKind;
  name: { de: string; en: string };
  price: number;
  /** null: until the condition (Growth Fund ends at HQ 25). */
  durationDays: number | null;
  drip?: { creditsPerDay: number; requiresLogin: boolean; extras?: Bundle; finalDay?: Bundle };
  perks: string[];
  /** Growth Fund payouts by HQ level. */
  payouts?: { hq: number; reward: Bundle }[];
  /** Unit / fabrication passes: milestones on a counter (unit XP levels, pulls). */
  milestones?: { at: number; reward: Bundle }[];
  /** Battle pass premium tiers. */
  premiumPlus?: { price: number; extraTiers: number; skin: string };
}

export interface WheelSegment {
  id: string;
  weight: number;
  reward: Bundle;
  jackpot?: boolean;
}

export interface VaultRoundDef {
  /** Normal prizes on the board (shuffled per round). */
  prizes: Bundle[];
  grand: Bundle;
  treasure: boolean;
}

export interface CouponDef {
  pct: number;
  weight: number;
}

export interface CapsuleDef {
  id: string;
  weight: number;
  reward: Bundle;
  /** Coloured chest capsules need a key of the same colour to open. */
  chest?: "green" | "blue" | "purple" | "gold";
}

export type ShopCurrency = "ARC" | "PRM" | "SLV" | "VIP" | "EVT" | "SCR" | "CREDITS" | "ALC";

export interface ShopItemDef {
  id: string;
  reward: Bundle;
  price: number;
  /** Purchases per refresh period. */
  limit: number;
  /** VIP level required (VIP shop). */
  vipLevel?: number;
  /** Alliance building levels required (alliance shop, D72). */
  requires?: { tradeHub?: number };
  /** Discount shown as a ribbon (VIP shop, Black Market). */
  discountPct?: number;
}

export interface ShopDef {
  id: string;
  name: { de: string; en: string };
  currency: ShopCurrency;
  refresh: "daily" | "weekly" | "event";
  items: ShopItemDef[];
}

export type CosmeticKind = "base_skin" | "building_skin" | "unit_skin" | "frame" | "name_colour" | "chat_bubble" | "emote_pack" | "march_trail" | "stinger";

export interface CosmeticDef {
  id: string;
  kind: CosmeticKind;
  name: { de: string; en: string };
  price: number;
  /** Building or unit id the skin applies to. */
  target?: string;
  /** Asset id rendered in the preview (a building tier model, a unit, a crate). */
  asset?: string;
  bonus?: { stat: string; pct: number };
  colour?: string;
}

export const StoreTabSchema = z.enum(["featured", "packs", "daily", "passes", "speedups", "resources", "fabrication", "vip", "cosmetics", "shops"]);
export type StoreTab = z.infer<typeof StoreTabSchema>;

export const LocalizedName = LocalizedTextSchema;
